import React from 'react'

import { PercentAreaChart } from 'features/charts-v2/components/PercentAreaChart'
import { dataTransformationService } from 'features/charts-v2/services/data-transformation-service'
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import { METRICS, type MetricType } from 'features/charts-v2/types'
import {
  ChartProps,
  TransformedChartData,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'

export class PercentAreaChartStrategy extends BaseChartStrategy {
  public readonly chartType = ChartTypeV2.AREA_PERCENT
  public readonly requiredMetrics: MetricType[] = [METRICS.RECORD_COUNT.id]
  public readonly optionalMetrics: MetricType[] = [METRICS.SENTENCE_COUNT.id]

  public transform(props: ChartProps): TransformedChartData {
    const breakdowns = props.selectedBreakdowns || []

    // Handle 0 breakdowns: show single area at 100% over time
    if (breakdowns.length === 0) {
      // Use basic chart transformation which works for time-series data
      return dataTransformationService.transformForBasicChart(
        props.data!,
        props.selectedMetrics,
        props.timeBucket,
      )
    }

    return dataTransformationService.transformForStackedArea(
      props.data!,
      props.selectedMetrics,
      props.timeBucket,
    )
  }

  public render(
    props: ChartProps & { transformedData: TransformedChartData },
  ): React.ReactElement {
    const validation = this.validate(props)
    if (!validation.isValid) {
      return this.renderValidationError(validation)
    }

    const {
      transformedData,
      hoveredDataPoint,
      onDataPointHover,
      selectedMetrics,
      onSegmentClick,
      fontSizePx = 13,
      showXAxisLabel = true,
      showYAxisLabel = true,
      showDatapointLabels = true,
      xAxisOverflow = 'expand',
      yAxisOverflow = 'expand',
      disableAnimation = false,
    } = props

    if (!transformedData.chartData || transformedData.chartData.length === 0) {
      return this.renderEmptyState(
        'No data available',
        'Try adding some filters.',
      )
    }

    const breakdowns = props.selectedBreakdowns || []
    const breakdownCategories =
      transformedData.metadata?.breakdownCategories || []

    // For 0 breakdowns, use a single category "Total" at 100%
    const effectiveCategories =
      breakdowns.length === 0 ? ['count'] : breakdownCategories

    return (
      <PercentAreaChart
        data={transformedData.chartData as never[]}
        breakdownCategories={effectiveCategories}
        selectedMetrics={selectedMetrics}
        onDataPointHover={onDataPointHover}
        hoveredDataPoint={hoveredDataPoint}
        onSegmentClick={onSegmentClick as never}
        fontSizePx={fontSizePx}
        showXAxisLabel={showXAxisLabel}
        showYAxisLabel={showYAxisLabel}
        showDatapointLabels={showDatapointLabels}
        xAxisOverflow={xAxisOverflow}
        yAxisOverflow={yAxisOverflow}
        disableAnimation={disableAnimation}
      />
    )
  }

  public getTitle(): string {
    return 'Breakdown by Category (100% Area)'
  }
}
